import React, { useState, useEffect } from 'react';
import { Mic, CheckCircle, XCircle } from 'lucide-react';
import { useWebSocket } from '../../../WebSocketContext';

const MicrophoneStep = ({ permissionStatus, checkMicrophonePermission, audioData, isMobile }) => {
  const [permissionSuccessSent, setPermissionSuccessSent] = useState(false);
  const { sendMicrophonePermissionSuccess } = useWebSocket();
  
  // Send microphone permission success event when microphone access is granted
  useEffect(() => {
    if (permissionStatus.microphone === 'granted' && !permissionSuccessSent) {
      sendMicrophonePermissionSuccess();
      setPermissionSuccessSent(true);
    }
  }, [permissionStatus.microphone, sendMicrophonePermissionSuccess, permissionSuccessSent]);

  return (
    <div className="flex flex-col items-center">
      <h2 className={`${isMobile ? 'text-sm' : 'text-md'} font-semibold text-gray-900 ${isMobile ? 'mb-4' : 'mb-6'}`}>Microphone Check</h2>
      <div className={`${isMobile ? 'mb-4 p-4' : 'mb-6 p-6'} bg-gray-50 rounded-full`}>
        {permissionStatus.microphone === 'granted' ? (
          <CheckCircle size={isMobile ? 48 : 64} className="text-green-600" />
        ) : permissionStatus.microphone === 'denied' ? (
          <XCircle size={isMobile ? 48 : 64} className="text-red-600" />
        ) : (
          <Mic size={isMobile ? 48 : 64} className={`text-indigo-600 ${permissionStatus.microphone === 'checking' ? 'animate-pulse' : ''}`} />
        )}
      </div>

      {permissionStatus.microphone === 'granted' ? (
        <div className="flex flex-col items-center p-4 bg-green-50 text-green-700 rounded-md w-full max-w-md">
          <p className={`font-medium mb-4 ${isMobile ? 'text-xs' : 'text-sm'}`}>Microphone access granted!</p>
          <div className="w-full max-w-md">
            {/* New colorful waveform visualization that mimics the image */}
            <div className="mb-4">
              <svg width={isMobile ? "200" : "250"} height={isMobile ? "100" : "120"} viewBox="0 0 300 120" className="mx-auto">
                {/* Define gradients for the waveforms */}
                <defs>
                  <linearGradient id="gradientTop" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#00BFFF" />
                    <stop offset="50%" stopColor="#9370DB" />
                    <stop offset="100%" stopColor="#FF69B4" />
                  </linearGradient>
                  <linearGradient id="gradientBottom" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#00BFFF" />
                    <stop offset="50%" stopColor="#9370DB" />
                    <stop offset="100%" stopColor="#FF69B4" />
                  </linearGradient>

                  {/* Add glow effect */}
                  <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Horizontal center line */}
                <line x1="0" y1="60" x2="300" y2="60" stroke="#333" strokeWidth="1" />

                {/* Top waveform (mirrored from bottom) */}
                <path
                  d={`M 0,60 ${audioData.waveform.map((point, i) => {
                    const x = (i / (audioData.waveform.length - 1)) * 300;
                    // Amplify the wave based on audio level and make it go upward from center
                    const amplifier = 40 * (0.2 + audioData.level * 10);
                    const y = 60 - Math.abs(point * amplifier);
                    return `L ${x},${y}`;
                  }).join(' ')}`}
                  stroke="url(#gradientTop)"
                  strokeWidth="4"
                  fill="none"
                  filter="url(#glow)"
                />

                {/* Bottom waveform */}
                <path
                  d={`M 0,60 ${audioData.waveform.map((point, i) => {
                    const x = (i / (audioData.waveform.length - 1)) * 300;
                    // Amplify the wave based on audio level and make it go downward from center
                    const amplifier = 40 * (0.2 + audioData.level * 10);
                    const y = 60 + Math.abs(point * amplifier);
                    return `L ${x},${y}`;
                  }).join(' ')}`}
                  stroke="url(#gradientBottom)"
                  strokeWidth="4"
                  fill="none"
                  filter="url(#glow)"
                />
              </svg>
            </div>
            <p className={`text-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>Try speaking to see the audio waveform</p>
          </div>
        </div>
      ) : permissionStatus.microphone === 'denied' ? (
        <div className="p-4 bg-red-50 text-red-700 rounded-md w-full max-w-md">
          <p className={`mb-4 ${isMobile ? 'text-xs' : 'text-sm'}`}>Microphone access denied. Please allow access in your browser settings and try again.</p>
          <button
            className={`px-4 ${isMobile ? 'py-1.5 text-xs' : 'py-2 text-sm'} bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors`}
            onClick={checkMicrophonePermission}
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center p-4 bg-indigo-50 text-blue-700 rounded-md w-full max-w-md">
          <p className={`mb-4 text-center ${isMobile ? 'text-xs' : 'text-sm'}`}>Please allow access to your microphone when prompted by your browser.</p>
          {permissionStatus.microphone === 'pending' && (
            <button
              className={`px-4 ${isMobile ? 'py-1.5 text-xs' : 'py-2 text-sm'} bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors`}
              onClick={checkMicrophonePermission}
            >
              Check Microphone
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MicrophoneStep;
